import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Grid, TextField, MenuItem, Button, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiServices';
import PageHeader from '../common/PageHeader';
import { printReport, currencyCol, textCol } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';
import { Link } from 'react-router-dom';
import { ReportPage, ExportMenu } from './ReportKit';

export default function ATKReportsKS() {
  const { t } = useTranslation();
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState('');
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState(0); // 0=TM1,1=TM2,2=TM3,3=TM4

  const [rates, setRates] = useState({ pensionEmployee: 0.05, pensionEmployer: 0.05 });
  const [policyName, setPolicyName] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    apiService().get('salariesmonthlylists').then((r) => r?.success && setLists(r.data || []));
    // Ngarko normat e pensionit nga politika aktive fiskale (jo manual)
    apiService().query('getActiveFiscalPolicy', '').then((r) => {
      if (r?.success && r.data?.length) {
        const p = r.data[0];
        setRates({
          pensionEmployee: (Number(p.PensionEmployee) || 5) / 100,
          pensionEmployer: (Number(p.PensionEmployer) || 5) / 100,
        });
        setPolicyName(p.Name || '');
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!listId) return setRows([]);
    apiService().query('getSalariesByListId', `id=${listId}`).then((r) => r?.success && setRows(r.data || []));
  }, [listId]);

  const fullName = (r) => r?.FullName || r?.fullName || `${r?.FirstName || r?.firstname || ''} ${r?.LastName || r?.lastname || ''}`.trim();

  const computed = useMemo(() => {
    return rows.map((r) => {
      const gross = (Number(r.Salary)||0) + (Number(r.HourSalary)||0) + (Number(r.Bonus)||0) - (Number(r.Penalties)||0);
      const penEmp = +(gross * rates.pensionEmployee).toFixed(2);
      const penEr = +(gross * rates.pensionEmployer).toFixed(2);
      const tax = Number(r.taxes)||0; // prefer server tax if present
      const net = Number(r.neto)|| (gross - penEmp - tax);
      return { ...r, gross, penEmp, penEr, tax, net };
    });
  }, [rows, rates]);

  function exportCsv(prefix) {
    const headers = [t('report.nr'), t('report.name'), t('report.name'), t('report.gross'), t('report.kbpkPensionWorker'), t('report.kbpkPensionEmployer'), t('report.tax'), t('report.net')];
    const lines = [headers.join(',')];
    computed.forEach((r) => {
      const [fn, ln] = fullName(r).split(' ');
      const line = [r.Id || '', fn||'', ln||'', r.gross, r.penEmp, r.penEr, r.tax, r.net]
        .map((x) => `"${String(x).replace(/"/g,'""')}"`).join(',');
      lines.push(line);
    });
    const csv = lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${prefix}_TM${tab+1}.csv`; a.click(); URL.revokeObjectURL(url);
  }

  function exportXls(prefix){
    const headers=[t('report.nr'), t('report.worker'), t('report.gross'), t('report.kbpkPensionWorker'), t('report.kbpkPensionEmployer'), t('report.tax'), t('report.net')];
    const rowsHtml = computed.map(r=>{
      const name = fullName(r);
      return `<tr><td>${r.Id||''}</td><td>${name}</td><td>${r.gross}</td><td>${r.penEmp}</td><td>${r.penEr}</td><td>${r.tax}</td><td>${r.net}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset='UTF-8'></head><body><table border='1'><thead><tr>${headers.map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${rowsHtml}</tbody></table></body></html>`;
    const blob = new Blob([html], { type:'application/vnd.ms-excel' }); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download=`${prefix}_TM${tab+1}.xls`; a.click(); URL.revokeObjectURL(url);
  }

  function exportPdf() {
    const tmLabel = `TM${tab + 1}`;
    const selectedList = lists.find((l) => String(l.id) === String(listId));
    const period = selectedList ? `${selectedList.month}/${selectedList.year}` : '';
    const pdfRows = computed.map((r) => ({ ...r, _name: fullName(r) }));
    const sumOf = (k) => pdfRows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    printReport({
      title: `${t('reports.atkTM')} — TM${tab + 1}`,
      subtitle: `${t('report.salary')} ${period ? `| ${t('report.period')}: ${period}` : ''}`,
      orientation: 'landscape',
      columns: [
        textCol(t('report.worker'), '_name', '28%'),
        currencyCol(t('report.gross'), 'gross', '14%'),
        currencyCol(t('report.kbpkPensionWorker'), 'penEmp', '16%'),
        currencyCol(t('report.kbpkPensionEmployer'), 'penEr', '16%'),
        currencyCol(t('report.tax'), 'tax', '13%'),
        currencyCol(t('report.net'), 'net', '13%'),
      ],
      rows: pdfRows,
      totals: { gross: sumOf('gross'), penEmp: sumOf('penEmp'), penEr: sumOf('penEr'), tax: sumOf('tax'), net: sumOf('net') },
      period,
      badge: { label: `${computed.length} ${t('report.workers')}` },
      extraInfo: [`Politika: ${policyName || 'Standard'}`, `Pension: ${(rates.pensionEmployee * 100).toFixed(1)}% + ${(rates.pensionEmployer * 100).toFixed(1)}%`],
    });
  }

  function fmt(n) { return new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR' }).format(n||0); }

  return (
    <ReportPage>
      <PageHeader title={t('reports.atkTM')}
        actions={<Button component={Link} to="/home/reports" variant="outlined">{t('report.backLabel')}</Button>} />
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField select fullWidth label={t('report.selectList')} value={listId} onChange={(e) => setListId(e.target.value)}>
                {lists.map((l) => <MenuItem key={l.id} value={l.id}>{`${l.month}/${l.year}`}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" disabled
                label={t('report.pensionWorkerPct')}
                value={`${(rates.pensionEmployee * 100).toFixed(1)}%`}
                helperText={policyName || 'Politika aktive fiskale'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" disabled
                label={t('report.pensionEmployerPct')}
                value={`${(rates.pensionEmployer * 100).toFixed(1)}%`}
                helperText="Normat vendosen te: Konfigurime → Politikat Fiskale"
              />
            </Grid>
            <Grid item xs={12} sm="auto"><ExportMenu onCsv={() => exportCsv('atk')} onExcel={() => exportXls('atk')} onPdf={() => exportPdf()} onEmail={() => setEmailOpen(true)} disabled={!listId || computed.length === 0} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{ mb: 1 }}>
        <Tab label="TM1" /><Tab label="TM2" /><Tab label="TM3" /><Tab label="TM4" />
      </Tabs>

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('report.worker')}</TableCell>
                <TableCell align="right">{t('report.gross')}</TableCell>
                <TableCell align="right">{t('report.kbpkPensionWorker')}</TableCell>
                <TableCell align="right">{t('report.kbpkPensionEmployer')}</TableCell>
                <TableCell align="right">{t('report.tax')}</TableCell>
                <TableCell align="right">{t('report.net')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {computed.map((r) => (
                <TableRow key={r.Id}>
                  <TableCell>{r.FirstName} {r.LastName}</TableCell>
                  <TableCell align="right">{fmt(r.gross)}</TableCell>
                  <TableCell align="right">{fmt(r.penEmp)}</TableCell>
                  <TableCell align="right">{fmt(r.penEr)}</TableCell>
                  <TableCell align="right">{fmt(r.tax)}</TableCell>
                  <TableCell align="right">{fmt(r.net)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={`${t('reports.atkTM')} — TM${tab + 1}`}
        subtitle={listId ? `Lista #${listId}` : ''}
        columns={[
          { label: t('report.worker'), key: r => `${r.FirstName} ${r.LastName}`, align: 'left' },
          { label: t('report.gross'), key: 'gross', align: 'right', format: 'currency' },
          { label: t('report.kbpkPensionWorker'), key: 'penEmp', align: 'right', format: 'currency' },
          { label: t('report.kbpkPensionEmployer'), key: 'penEr', align: 'right', format: 'currency' },
          { label: t('report.tax'), key: 'tax', align: 'right', format: 'currency' },
          { label: t('report.net'), key: 'net', align: 'right', format: 'currency' },
        ]}
        rows={computed}
        workers={rows}
      />
    </ReportPage>
  );
}
